/**
 * 2026-09-22 — Tek sağlayıcı kataloğu (etiket + yönetim sayfası yolu).
 *
 * The overview's provider list comes from the SERVER (registered bundles), so a
 * newly enabled provider shows up without a client release. This catalogue only
 * supplies the operator label and the control-plane path; an id it does not
 * know falls back to its raw id and the generic /providers/:id page.
 *
 * Labels and paths mirror shell/sidebar.tsx (SECTIONS) and the routes in
 * App.tsx; keep them in sync when a provider is added.
 */

export type ProviderFamily = 'alias' | 'mailbox';

export interface ProviderCatalogEntry {
  readonly id: string;
  readonly label: string;
  readonly family: ProviderFamily;
  /** SPA path of the provider's control plane (router basename '/admin'). */
  readonly path: string;
  /** Reads mail through the residential SOCKS5 IMAP gateway. */
  readonly residentialGateway: boolean;
  /**
   * D-246 — passive line fallback (sales + renewal off, read-only page). The
   * overview/summary payloads carry the server's own `passive` flag; this marks
   * the SPA-side entries (sidebar strike-through) that exist only as a constant.
   */
  readonly passive?: boolean;
}

export const PROVIDER_CATALOG: readonly ProviderCatalogEntry[] = [
  { id: 'mailcom', label: 'mail.com', family: 'alias', path: '/mailcom-control-plane', residentialGateway: false },
  { id: 'gmx', label: 'gmx.com', family: 'alias', path: '/gmx-control-plane', residentialGateway: false, passive: true },
  // D-247: GMX Mailbox — FIRST entry of the Mailbox family. Reads GMX over the
  // shared saved residential gateway (D-245), edited on the t-online page.
  { id: 'gmxbox', label: 'GMX', family: 'mailbox', path: '/gmxbox-control-plane', residentialGateway: true },
  { id: 'rambler', label: 'rambler.ru', family: 'mailbox', path: '/rambler-control-plane', residentialGateway: false },
  { id: 'aol', label: 'aol.com', family: 'mailbox', path: '/aol-control-plane', residentialGateway: false },
  { id: 'webde', label: 'web.de', family: 'mailbox', path: '/webde-control-plane', residentialGateway: true },
  { id: 'libero', label: 'libero.it', family: 'mailbox', path: '/libero-control-plane', residentialGateway: false },
  { id: 'tiscaliit', label: 'tiscali.it', family: 'mailbox', path: '/tiscaliit-control-plane', residentialGateway: false },
  { id: 'tiscalicz', label: 'tiscali.cz', family: 'mailbox', path: '/tiscalicz-control-plane', residentialGateway: true },
  { id: 'sinavip', label: 'vip.sina.com', family: 'mailbox', path: '/sinavip-control-plane', residentialGateway: false },
  { id: 'sina', label: 'sina.com', family: 'mailbox', path: '/sina-control-plane', residentialGateway: false },
  { id: 'gazetapl', label: 'gazeta.pl', family: 'mailbox', path: '/gazetapl-control-plane', residentialGateway: false },
  { id: 'rediffmail', label: 'rediffmail.com', family: 'mailbox', path: '/rediffmail-control-plane', residentialGateway: false },
  { id: 'tonline', label: 't-online.de', family: 'mailbox', path: '/tonline-control-plane', residentialGateway: true },
];

const BY_ID: ReadonlyMap<string, ProviderCatalogEntry> = new Map(PROVIDER_CATALOG.map((e) => [e.id, e]));

export function providerCatalogEntry(id: string | null | undefined): ProviderCatalogEntry | null {
  if (!id) return null;
  return BY_ID.get(id) ?? null;
}

/** Operator label: catalogue label, else the server's label, else the raw id. */
export function providerLabel(id: string | null | undefined, serverLabel?: string | null): string {
  const entry = providerCatalogEntry(id);
  if (entry) return entry.label;
  if (serverLabel && serverLabel.length > 0) return serverLabel;
  return id ?? '—';
}

/** D-246 — true when the catalogue marks the provider line passive (read-only). */
export function providerIsPassive(id: string | null | undefined): boolean {
  return providerCatalogEntry(id)?.passive === true;
}

/** Control-plane path; an unknown id falls back to the generic health page. */
export function providerPath(id: string): string {
  return providerCatalogEntry(id)?.path ?? `/providers/${encodeURIComponent(id)}`;
}

/** Comma-joined labels for a list (or CSV string) of provider ids. */
export function providerLabels(ids: readonly string[] | string | null | undefined): string {
  const list = typeof ids === 'string' ? ids.split(',') : ids ?? [];
  return list
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => providerLabel(s))
    .join(', ');
}
